import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiChevronLeft,
  FiChevronRight as FiNext,
} from "react-icons/fi";

import {
  getAutoPoolUsers,
  getAutoPoolStatus,
} from "../../store/slices/pool";

const AutoPoolList = () => {
  const dispatch = useDispatch();

  /* =====================================================
     PAGINATION
  ===================================================== */

  const [currentPage, setCurrentPage] = useState(1);

  const limit = 25;


  /* =====================================================
     POOL FILTER
  ===================================================== */

  const [selectedPool, setSelectedPool] = useState("");


  /* =====================================================
     REDUX
  ===================================================== */

  const {
    autoPoolUsers = [],
    autoPoolUsersData,
    autoPoolUsersLoading,

    autoPoolStatus,

    autoPoolStatusLoading,

    error,
  } = useSelector(
    (state) => state.pool || {}
  );


  /* =====================================================
     GET AUTO POOL STATUS
     
     This is only used to populate the dropdown.
     It does NOT send pool to getAutoPoolUsers.
  ===================================================== */

  useEffect(() => {
    dispatch(getAutoPoolStatus());
  }, [dispatch]);


  /* =====================================================
     GET USERS
     
     IMPORTANT:
     
     Initial request:
       page=1
       limit=25
     
     No pool is sent.
     
     After selecting a pool:
       pool=selectedPool
       page=currentPage
       limit=25
  ===================================================== */

  useEffect(() => {

    const params = {
      page: currentPage,
      limit,
    };


    /* =====================================================
       ONLY ADD POOL WHEN USER SELECTS A POOL
    ===================================================== */

    if (
      selectedPool !== "" &&
      selectedPool !== null &&
      selectedPool !== undefined
    ) {
      params.pool = selectedPool;
    }


    dispatch(
      getAutoPoolUsers(params)
    );

  }, [
    dispatch,
    currentPage,
    selectedPool,
  ]);


  /* =====================================================
     POOL OPTIONS
  ===================================================== */

  const poolOptions = useMemo(() => {

    /*
     * Try to get pools from API response.
     *
     * Supports different possible structures:
     *
     * data.pools
     * data.data.pools
     */

    const pools =
      autoPoolStatus?.data?.pools ||
      autoPoolStatus?.pools ||
      [];


    if (!Array.isArray(pools)) {
      return [];
    }


    return pools;

  }, [autoPoolStatus]);


  /* =====================================================
     HANDLE POOL CHANGE
  ===================================================== */

  const handlePoolChange = (event) => {

    const value = event.target.value;

    /*
     * Reset to page 1 whenever pool changes.
     */

    setCurrentPage(1);

    setSelectedPool(value);

  };


  /* =====================================================
     PAGINATION DATA
  ===================================================== */

  const pagination =
    autoPoolUsersData?.pagination || {};


  const totalUsers =
    Number(
      pagination?.totalUsers
    ) || 0;


  const totalPages =
    Number(
      pagination?.totalPages
    ) || 0;


  const apiCurrentPage =
    Number(
      pagination?.currentPage
    ) || currentPage;


  const apiLimit =
    Number(
      pagination?.limit
    ) || limit;


  const hasNextPage =
    pagination?.hasNextPage ??
    (
      apiCurrentPage <
      totalPages
    );


  const hasPreviousPage =
    pagination?.hasPreviousPage ??
    (
      apiCurrentPage > 1
    );


  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (date) => {

    if (!date) {
      return "-";
    }


    const parsedDate =
      new Date(date);


    if (
      Number.isNaN(
        parsedDate.getTime()
      )
    ) {
      return "-";
    }


    return parsedDate.toLocaleString(
      "en-IN",
      {
        dateStyle: "medium",
        timeStyle: "short",
      }
    );
  };


  /* =====================================================
     USERS
  ===================================================== */

  const users =
    Array.isArray(autoPoolUsers)
      ? autoPoolUsers
      : [];


  /* =====================================================
     PAGE CHANGE
  ===================================================== */

  const handlePageChange = (
    pageNumber
  ) => {

    if (
      pageNumber < 1 ||
      pageNumber > totalPages ||
      pageNumber === currentPage ||
      autoPoolUsersLoading
    ) {
      return;
    }


    setCurrentPage(
      pageNumber
    );


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

  };


  /* =====================================================
     PREVIOUS
  ===================================================== */

  const handlePrevious = () => {

    if (
      hasPreviousPage &&
      !autoPoolUsersLoading
    ) {

      handlePageChange(
        apiCurrentPage - 1
      );

    }

  };


  /* =====================================================
     NEXT
  ===================================================== */

  const handleNext = () => {

    if (
      hasNextPage &&
      !autoPoolUsersLoading
    ) {

      handlePageChange(
        apiCurrentPage + 1
      );

    }

  };


  /* =====================================================
     PAGINATION NUMBERS
  ===================================================== */

  const getPageNumbers = () => {

    const pages = [];


    if (totalPages <= 5) {

      for (
        let i = 1;
        i <= totalPages;
        i++
      ) {

        pages.push(i);

      }

      return pages;

    }


    if (apiCurrentPage <= 3) {

      pages.push(
        1,
        2,
        3,
        4,
        "...",
        totalPages
      );

      return pages;

    }


    if (
      apiCurrentPage >=
      totalPages - 2
    ) {

      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );

      return pages;

    }


    pages.push(
      1,
      "...",
      apiCurrentPage - 1,
      apiCurrentPage,
      apiCurrentPage + 1,
      "...",
      totalPages
    );


    return pages;

  };


  /* =====================================================
     DISPLAY RANGE
  ===================================================== */

  const displayStart =
    totalUsers > 0
      ? (
        (apiCurrentPage - 1) *
        apiLimit
      ) + 1
      : 0;


  const displayEnd =
    Math.min(
      apiCurrentPage *
      apiLimit,
      totalUsers
    );


  /* =====================================================
     RETURN
  ===================================================== */

  return (

    <div className="gi-referral-page">


      {/* =================================================
          BREADCRUMB
      ================================================= */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">

          <FiHome />

          Dashboard

        </Link>


        <FiChevronRight
          className="gi-breadcrumb-arrow"
        />


        <span>
          Auto Pool
        </span>

      </div>


      {/* =================================================
          TABLE CARD
      ================================================= */}

      <section className="gi-referral-table-card">


        {/* =================================================
            FILTER
        ================================================= */}

        <div
          className="gi-autopool-filter"
          style={{
            padding: "0 0 20px 0",
          }}
        >

          <select
            value={selectedPool}
            onChange={
              handlePoolChange
            }
            disabled={
              autoPoolUsersLoading
            }
            style={{
              width: "200px",
              maxWidth: "100%",
              height: "35px",
              padding: "0 18px",
              border:
                "1px solid #ddd",
              borderRadius: "8px",
              backgroundColor:
                "#fff",
              fontSize: "14px",
              fontWeight: "500",
              color: "#222",
              outline: "none",
              cursor: "pointer",
              margin: "10px"
            }}
          >

            {/* ALL */}

            <option value="">
              All Auto Pools
            </option>


            {/* POOLS */}

            {poolOptions.map(
              (pool, index) => {


                const poolValue =
                  typeof pool ===
                    "object"
                    ? (
                      pool.pool ??
                      pool.level ??
                      pool.autoPool ??
                      pool._id
                    )
                    : pool;


                const poolLabel =
                  typeof pool ===
                    "object"
                    ? (
                      pool.name ||
                      pool.title ||
                      `Auto Pool ${poolValue}`
                    )
                    : `Auto Pool ${pool}`;


                return (
                  <option
                    key={
                      poolValue ??
                      index
                    }
                    value={
                      poolValue
                    }
                  >
                    {poolLabel}
                  </option>
                );

              }
            )}

          </select>

        </div>


        {/* =================================================
            TABLE WRAPPER
        ================================================= */}

        <div className="gi-referral-table-wrapper">


          {/* =================================================
              LOADING
          ================================================= */}

          {autoPoolUsersLoading ? (

            <div className="gi-pool-members-loading">

              Loading members...

            </div>

          ) : error ? (

            /* =================================================
               ERROR
            ================================================= */

            <div className="gi-pool-members-loading">

              {error}

            </div>

          ) : users.length === 0 ? (

            /* =================================================
               NO DATA
            ================================================= */

            <div className="gi-pool-members-loading">

              No members found

            </div>

          ) : (

            <>


              {/* =================================================
                  TABLE
              ================================================= */}

              <table className="gi-referral-table">

                <thead>

                  <tr>

                    <th>
                      #
                    </th>


                    <th>
                      User ID
                    </th>


                    <th>
                      Auto Pool
                    </th>


                    <th>
                      Activation No
                    </th>


                    <th>
                      Name
                    </th>


                    <th>
                      Mobile
                    </th>


                    <th>
                      Email
                    </th>


                    <th>
                      Status
                    </th>


                    <th>
                      Activation Date
                    </th>

                  </tr>

                </thead>


                <tbody>

                  {users.map(
                    (
                      user,
                      index
                    ) => {

                      const serialNumber =
                        (
                          apiCurrentPage -
                          1
                        ) *
                        apiLimit +
                        index +
                        1;


                      return (

                        <tr
                          key={
                            user._id ||
                            user.userId ||
                            index
                          }
                        >


                          {/* # */}

                          <td>

                            {String(
                              serialNumber
                            ).padStart(
                              2,
                              "0"
                            )}

                          </td>


                          {/* USER ID */}

                          <td>

                            <strong>
                              {user.userId ||
                                "-"}

                            </strong>

                          </td>


                          {/* AUTO POOL */}

                          <td>

                            {user.autoPool ??
                              "-"}

                          </td>


                          {/* ACTIVATION NO */}

                          <td>

                            {user.autoPoolActivationNo ??
                              "-"}

                          </td>


                          {/* NAME */}

                          <td>

                            {user.name ||
                              "-"}

                          </td>


                          {/* MOBILE */}

                          <td>

                            {user.mobileNo ||
                              "-"}

                          </td>


                          {/* EMAIL */}

                          <td>

                            {user.email ||
                              "-"}

                          </td>


                          {/* STATUS */}

                          <td>

                            <span
                              className={
                                user.status ===
                                  "ACTIVE"
                                  ? "badge bg-success"
                                  : "badge bg-secondary"
                              }
                            >

                              {user.status ||
                                "-"}

                            </span>

                          </td>


                          {/* ACTIVATION DATE */}

                          <td>

                            {formatDate(
                              user.activationDate
                            )}

                          </td>


                        </tr>

                      );

                    }
                  )}

                </tbody>

              </table>


              {/* =================================================
                  PAGINATION
              ================================================= */}

              {totalPages > 0 && (

                <div className="gi-users-pagination">


                  {/* PAGINATION INFO */}

                  <div className="gi-pagination-info">

                    Showing{" "}

                    <strong>
                      {displayStart}
                    </strong>

                    {" "}to{" "}

                    <strong>
                      {displayEnd}
                    </strong>

                    {" "}of{" "}

                    <strong>
                      {totalUsers}
                    </strong>

                    {" "}users

                  </div>


                  {/* PAGINATION BUTTONS */}

                  <div className="gi-pagination-buttons">


                    {/* PREVIOUS */}

                    <button
                      type="button"
                      className="gi-pagination-btn"
                      disabled={
                        !hasPreviousPage ||
                        autoPoolUsersLoading
                      }
                      onClick={
                        handlePrevious
                      }
                    >

                      <FiChevronLeft />

                      <span>
                        Prev
                      </span>

                    </button>


                    {/* PAGE NUMBERS */}

                    {getPageNumbers().map(
                      (
                        pageNumber,
                        index
                      ) => {

                        if (
                          pageNumber ===
                          "..."
                        ) {

                          return (

                            <span
                              key={`dots-${index}`}
                              className="gi-pagination-dots"
                            >
                              ...
                            </span>

                          );

                        }


                        return (

                          <button
                            key={
                              pageNumber
                            }
                            type="button"
                            className={`gi-pagination-number ${apiCurrentPage ===
                              pageNumber
                              ? "active"
                              : ""
                              }`}
                            disabled={
                              autoPoolUsersLoading
                            }
                            onClick={() =>
                              handlePageChange(
                                pageNumber
                              )
                            }
                          >

                            {pageNumber}

                          </button>

                        );

                      }
                    )}


                    {/* NEXT */}

                    <button
                      type="button"
                      className="gi-pagination-btn"
                      disabled={
                        !hasNextPage ||
                        autoPoolUsersLoading
                      }
                      onClick={
                        handleNext
                      }
                    >

                      <span>
                        Next
                      </span>

                      <FiNext />

                    </button>


                  </div>

                </div>

              )}

            </>

          )}

        </div>

      </section>

    </div>

  );
};


export default AutoPoolList;